/**
 * Performance event capture for the web admin.
 *
 * Three trace kinds (matches the backend's CHECK constraint):
 *   * `app_start`   — cold-start from `performance.timing.navigationStart`
 *                     to first-meaningful-paint. Reported once on boot.
 *   * `screen_load` — React Router route change → time to next paint.
 *                     Wired via `useReportScreenLoad` hook called from a
 *                     top-level component.
 *   * `api_call`    — axios interceptor records every request's duration.
 *
 * Events are BUFFERED (≤20 per batch / 10 s flush timer) to cut the
 * round-trip count on a busy dashboard.
 */

import { postPerfBatch } from './api'

interface PerfEvent {
  kind: 'screen_load' | 'api_call' | 'app_start'
  name: string
  duration_ms: number
  status_code?: number
}

const buffer: PerfEvent[] = []
const MAX_BUFFER = 20
const FLUSH_MS = 10_000
let flushTimer: ReturnType<typeof setInterval> | null = null

function startFlushTimer(): void {
  if (flushTimer) return
  flushTimer = setInterval(flushBuffer, FLUSH_MS)
}

/** Teardown counterpart to the implicit timer-arming inside `record()`.
 *  The 2026-06-13 review §21 flagged the missing matching `clearInterval`
 *  — practically harmless (intervals die with the page), but exposing a
 *  named teardown lets tests reset module state explicitly and gives a
 *  future shutdown path a clean exit point. Drains any pending events
 *  first so we don't lose data buffered between the last 10s tick and
 *  the teardown call. */
export async function stopFlushTimer(): Promise<void> {
  if (flushTimer) {
    clearInterval(flushTimer)
    flushTimer = null
  }
  // Final drain — same pattern as the beforeunload/pagehide handlers in
  // `telemetry/index.ts`.
  await flushBuffer()
}

export async function flushBuffer(): Promise<void> {
  if (buffer.length === 0) return
  const batch = buffer.splice(0, buffer.length)
  await postPerfBatch(batch)
}

function record(e: PerfEvent): void {
  // Clamp obviously-broken values so a corrupted timer doesn't poison the
  // P95 charts.
  if (!Number.isFinite(e.duration_ms) || e.duration_ms < 0) return
  e.duration_ms = Math.min(Math.round(e.duration_ms), 600_000)
  buffer.push(e)
  if (buffer.length >= MAX_BUFFER) void flushBuffer()
  startFlushTimer()
}

/** Cold-start measurement. Call once on app boot. Uses the modern
 *  Navigation Timing API; falls back gracefully on older browsers. */
export function recordAppStart(): void {
  const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
  let durationMs: number
  if (nav && Number.isFinite(nav.domContentLoadedEventEnd)) {
    durationMs = nav.domContentLoadedEventEnd
  } else if (performance.timing) {
    durationMs = performance.timing.domContentLoadedEventEnd - performance.timing.navigationStart
  } else {
    durationMs = performance.now()
  }
  record({ kind: 'app_start', name: 'page_load', duration_ms: durationMs })
}

/** Per-route timing for the React Router location change. Pass a route
 *  template (`/matches/:id`) — NOT the resolved path — so aggregations
 *  group sanely. The caller (a top-level component that uses `useLocation`)
 *  templatizes via `pathToRoute(...)` below. */
export function recordScreenLoad(route: string, durationMs: number): void {
  record({ kind: 'screen_load', name: route, duration_ms: durationMs })
}

export function recordApiCall(args: {
  endpoint: string
  durationMs: number
  statusCode?: number
}): void {
  record({
    kind: 'api_call',
    name: args.endpoint,
    duration_ms: args.durationMs,
    status_code: args.statusCode,
  })
}

/** Replaces dynamic id segments with `:id`. Mirrors the mobile
 *  http_client.dart templatizer so admin App Health's "slow API" chart
 *  groups mobile and web rows by the same key. */
export function pathToRoute(path: string): string {
  return path
    .replace(/\/c[a-z0-9]{20,31}(?=\/|$)/g, '/:id')
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/g, '/:id')
    .replace(/\/\d+(?=\/|$)/g, '/:id')
}
